// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';

// Angular Material
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

// Third party
import * as _ from 'lodash';
import moment from 'moment';
import { combineLatest, take, takeUntil } from 'rxjs';

// Local library
import {
  CountdownPipe,
  GetEventStatusPipe,
  GetRaceStatusPipe,
  GetRunnerPipe,
  GetTrackTypePipe,
  NumberWithCommasPipe,
  RaceResultPipe,
  SortEventsPipe,
  StringToArraryPipe,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';
import {
  IGetEventsDetailsResponse,
  IPoolTotalDataModel,
  IRace,
  IRaceResult,
  TRACK_TYPE_lIST
} from '@amwager/util-ts';
import {
  CommonFacade,
  PermissionService,
  SessionService,
  ToteProgramAPIService,
} from '@amwager/data-access';
import { ProfileComponent } from '@amwager/account/feature-mat';
import { ProfileService } from '@amwager/account/data-access';
import {
  IBuildPoolTotalsResult,
  IPersonalStatistic,
  ISelectedRaceRaceResult,
  IStoragePoolTotal,
  RaceService,
  ResultsService,
  WagerFacade,
  WagerService,
} from '@amwager/wager/data-access';

// Local file
import { BetsComponent } from '../bets/bets.component';
import { MatrixComponent } from '../matrix/matrix.component';
import { ReplayVideoComponent } from '../replay-video/replay-video.component';

/**
 * Results page — renders final race board, My Bets and Pool Data for finished races
 */
@Component({
  selector: 'feature-mat-results',
  templateUrl: './results.component.html',
  styleUrls: ['./results.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    GetTrackTypePipe,
    NumberWithCommasPipe,
    RaceResultPipe,
    SortEventsPipe,
    StringToArraryPipe,
    GetRunnerPipe,
    GetEventStatusPipe,
    GetRaceStatusPipe,
    CountdownPipe,
    BetsComponent,
    MatrixComponent,
    ProfileComponent,
    TranslatePipe
  ],
  providers: [
    SortEventsPipe,
    GetEventStatusPipe,
  ]
})
export class ResultsComponent extends UnsubscribeObservableService
  implements OnInit, OnDestroy {

  readonly TRACK_TYPE_lIST = TRACK_TYPE_lIST;

  /** Scroll container for the horizontally-overflowing favorites list */
  @ViewChild('favorites') favorites!: ElementRef;

  /** Race pagination container — used to auto-scroll to the selected race */
  @ViewChild('swipeRaceSectionTemplateRef') swipeRaceSectionTemplateRef!: ElementRef;

  sortBy: string = 'Name';
  eventTypeFilter: string[] = [
    'Thoroughbred',
    'Quarterhorse',
    'Greyhound',
    'Harness',
    'International'
  ];

  /** Set of favorite event ids from account preferences */
  setListFavorites!: Set<string>;

  /** Event objects for the favorites bar chips */
  listOfFavoriteEvents: IGetEventsDetailsResponse[] = [];

  /** Raw unfiltered events list (used for favorites intersection) */
  private allEvents: IGetEventsDetailsResponse[] = [];

  /**
   * Live (today) events straight from the store. Backs the favorites
   * bar so it always mirrors the wager page — even while `allEvents`
   * is showing a historical date.
   */
  private liveEvents: IGetEventsDetailsResponse[] = [];

  /** Cached user email (`undefined` = unbootstrapped, `Guest` = anonymous) */
  userEmail?: string;

  events: IGetEventsDetailsResponse[] = [];
  selectedEvent?: IGetEventsDetailsResponse;
  selectedRace?: IRace;

  raceResults?: IRaceResult | null;
  poolsTotal?: IPoolTotalDataModel[] = [];
  poolPrices?: any[] = [];
  totalHandle?: number = 0;
  personalStatistic?: IPersonalStatistic;
  runners?: { Runners: any[] };

  storagePoolTotal: IStoragePoolTotal[] = [];

  isFetchingEventsData: boolean = true;
  isFetchingData: boolean = false;
  invalidDate: boolean = false;
  eventResultIsFalse: boolean = false;
  errMsg: string = '';

  /**
   * Marks the events list as coming from a historical-date fetch
   * (not today's store snapshot). While true, the store subscription
   * is ignored so today's shared events don't overwrite the results
   * we just fetched by date.
   */
  private isViewingPastDate: boolean = false;

  /**
   * Flips true on the first favorites emission (even empty).
   * Distinguishes "still loading" from "user has no favorites".
   */
  private hasEverLoadedFavorites = false;

  /**
   * Flips true on the first events store emission (even empty).
   * Gates the skeleton so refreshes don't flash back to it.
   */
  private hasEverReceivedEventsEmission = false;

  /**
   * Flips true on the first race-result payload that matches the
   * current selection (success OR failure). Sticky — subsequent
   * race switches use {@link showResultsSkeleton} for per-race
   * loading, not the full-page skeleton.
   */
  private hasEverLoadedRaceResult = false;

  /**
   * Flips true a short delay after mount. Enforces a minimum
   * skeleton time so fast/cached emissions don't cause a flash
   * on nav-back, and gives observers time to fire fresh API
   * calls before content is revealed.
   */
  private hasMinDisplayTimeElapsed = false;

  /** Timer handle for {@link hasMinDisplayTimeElapsed}; cleared on destroy. */
  private minDisplayTimeoutId?: ReturnType<typeof setTimeout>;

  // Bound identifiers derived from the current selection.
  raceId: number = 0;
  eventId: string = '';
  runId: string = '';

  today = moment().format('YYYY-MM-DD');
  maxDate = new Date();
  selectedDate: string = moment().format('MM-DD-YYYY');

  // True when inline profile panel is open — hides main results content
  isProfileOpen: boolean = false;

  getEventResultsForm!: FormGroup;

  private raceResultTimeout: any;

  private replayPending: boolean = false;
  private replayDialogRef?: MatDialogRef<ReplayVideoComponent>;

  /**
   * Pre-selection captured from the wager page's "click Finished
   * race" hand-off. Consumed once by {@link observeEvents} so we
   * land on the exact event + race the user clicked instead of
   * defaulting to `finished[0]`.
   */
  private pendingInitialSelection?: {
    eventId?: string;
    runId?: string;
    raceId?: number;
    date?: string;
  };

  get formVal() { return this.getEventResultsForm?.controls }

  /**
   * True on first landing OR after a date/today reset (event
   * cleared, no cached list). Also true until:
   *   - min display time has elapsed AND
   *   - first race result has arrived (when finished events exist)
   * so nav-back never shows "loaded shell / empty tables".
   * Not re-enabled by background refreshes — content stays put.
   * @returns {boolean} true = show full-page skeleton
   */
  get showInitialSkeleton(): boolean {
    if (this.invalidDate) return false;

    const stillLoadingInitial = (
      this.isFetchingEventsData && !this.hasEverReceivedEventsEmission
    );

    // Date/today reset explicitly clears selectedEvent + events.
    const isRefetchAfterReset = (
      this.isFetchingEventsData && !this.selectedEvent && this.events.length === 0
    );

    // Wait for the first race-result payload — only when we
    // actually have finished events to show. Empty list means
    // nothing to fetch, so skip this gate.
    const waitingForFirstResult = (
      this.events.length > 0 && !this.hasEverLoadedRaceResult
    );

    return (
      stillLoadingInitial
      || isRefetchAfterReset
      || !this.hasMinDisplayTimeElapsed
      || waitingForFirstResult
    );
  }

  /**
   * True on the first paint of a new race (fetching + no data).
   * Refreshes keep the previously rendered tables visible.
   * @returns {boolean} true = show per-race tables skeleton
   */
  get showResultsSkeleton(): boolean {
    return Boolean(
      this.isFetchingData
      && !this.raceResults
      && !this.errMsg
      && !this.eventResultIsFalse
    );
  }

  /**
   * True only while the preferences stream hasn't emitted yet.
   * Hidden for guests (favorites are a signed-in feature).
   * @returns {boolean} true = show skeleton chips
   */
  get showFavoritesSkeleton(): boolean {
    if (!this.userEmail || this.userEmail === 'Guest') return false;

    return !this.hasEverLoadedFavorites;
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly wagerFacade: WagerFacade,
    private readonly wagerService: WagerService,
    private readonly resultsService: ResultsService,
    private readonly toteProgramAPIService: ToteProgramAPIService,
    private readonly commonFacade: CommonFacade,
    private readonly sessionService: SessionService,
    private readonly dialog: MatDialog,
    readonly cdRef: ChangeDetectorRef,
    readonly router: Router,
    readonly permissionService: PermissionService,
    private readonly profileService: ProfileService,
    private readonly raceService: RaceService,
  ) {
    super();

    // Route state pushed by wager's Finished race click or the
    // replay-video "View Result" hand-off. Only available during
    // the navigation itself, so grab it here.
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras?.state as
      { eventId?: string; runId?: string; raceId?: number; date?: string } | undefined;
    if (state?.eventId || state?.raceId || state?.date) {
      this.pendingInitialSelection = {
        eventId: state?.eventId,
        runId: state?.runId,
        raceId: state?.raceId,
        date: state?.date,
      };
    }
  }

  ngOnInit(): void {
    // Favorites bar gate — hidden for guests / pre-session.
    this.userEmail = this.sessionService.getUserEmail();

    this.initForm();

    // Apply any hand-off date (e.g. replay "View Result") BEFORE
    // subscribing to today's events store so a past-date fetch
    // isn't clobbered by the initial store emission.
    this.applyPendingDateIfAny();

    this.observeEvents();
    this.observeResultsAndDetail();
    this.observeReplayVideo();
    this.observeFavoritesPreference();

    // Mirror the profile panel visibility from the shared service
    this.profileService.showProfile$
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((show: boolean) => {
        this.isProfileOpen = show;
        this.cdRef.markForCheck();
      });

    // Min-skeleton-time (~500 ms). Prevents flash on nav-back
    // when cached emissions are instant, and gives the result
    // API time to complete before content is revealed.
    this.minDisplayTimeoutId = setTimeout(() => {
      this.hasMinDisplayTimeElapsed = true;
      this.cdRef.markForCheck();
    }, 500);
  }

  override ngOnDestroy(): void {
    this.toUnsubscribe();

    if (this.raceResultTimeout) clearTimeout(this.raceResultTimeout);

    // Clear the min-display timer so it can't fire post-destroy.
    if (this.minDisplayTimeoutId) {
      clearTimeout(this.minDisplayTimeoutId);
      this.minDisplayTimeoutId = undefined;
    }

    // Close the replay dialog and clear the pending flag so a
    // late store emission can't reopen it after teardown
    if (this.replayDialogRef) {
      this.replayDialogRef.close();
      this.replayDialogRef = undefined;
    }
    this.replayPending = false;
  }

  /**
   * Wire the date input up. Store the value as a real `Date` so the
   * `matDatepicker` can round-trip it through its `DateAdapter` — a
   * pre-formatted string is rejected by `NativeDateAdapter.deserialize`
   * and blanks the input after every selection.
   */
  private initForm(): void {
    this.getEventResultsForm = this.formBuilder.group({
      dateBy: [new Date(), [Validators.required]]
    });
  }

  /**
   * Honor a `date` handed off via router state (e.g. replay video's
   * "View Result"). Pins the picker to that day and, when it's a
   * past date, kicks off a by-date fetch so we don't fall back to
   * today's finished races.
   * @returns {void}
   */
  private applyPendingDateIfAny(): void {
    const rawDate = this.pendingInitialSelection?.date;
    if (!rawDate) return;

    const parsed = moment(rawDate);
    if (!parsed.isValid() || parsed.isAfter(moment(), 'day')) return;

    const normalized = parsed.format('MM-DD-YYYY');
    this.selectedDate = normalized;
    this.formVal['dateBy']?.setValue(parsed.toDate());

    // Today already flows through the store subscription; only
    // historical dates need the by-date fetch path.
    if (parsed.isSame(moment(), 'day')) return;

    this.isViewingPastDate = true;
    this.isFetchingEventsData = true;
    this.selectedEvent = undefined;
    this.selectedRace = undefined;
    this.fetchEventsByDate(normalized);
  }

  /**
   * Subscribe to events list, filter to finished races, auto-select first
   * @returns {void}
   */
  private observeEvents(): void {
    this.wagerFacade.getSelectAllEventTrackRace$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((allEvents?: IGetEventsDetailsResponse[]) => {
        // Favorites always track the live store so they match the
        // wager page, even while the results table shows a past date.
        this.liveEvents = allEvents || [];
        this.rebuildFavoriteEvents();

        // Historical date is driving `events` via a direct API call;
        // ignore the store so today's list can't clobber it.
        if (this.isViewingPastDate) {
          this.cdRef.markForCheck();
          return;
        }

        this.processEvents(allEvents);
      });
  }

  /**
   * Hydrate `events` from an events list — used by both the today
   * store subscription and the historical-date API fetch.
   * @param {IGetEventsDetailsResponse[]} allEvents Events to consume
   * @returns {void}
   */
  private processEvents(allEvents?: IGetEventsDetailsResponse[] | null): void {
    // Raw list backs the favorites bar (all tracks, not just
    // finished-only ones — see `listOfFavoriteEvents` docs).
    this.allEvents = allEvents || [];

    // Flip the skeleton gate off once we've received an emission.
    this.hasEverReceivedEventsEmission = true;

    const finished = this.resultsService.filterFinishedEvents(allEvents);

    this.events = finished;
    this.isFetchingEventsData = false;

    this.rebuildFavoriteEvents();

    if (_.isEmpty(finished)) {
      this.selectedEvent = undefined;
      this.selectedRace = undefined;
      this.raceResults = null;
      this.errMsg = 'No finished races available.';
      this.cdRef.markForCheck();
      return;
    }

    // Clear any stale "no results" message from a prior empty list.
    this.errMsg = '';

    const stillPresent = _.find(
      finished, (event) => event?.id === this.selectedEvent?.id
    );

    if (!stillPresent) {
      // Prefer the event/race handed off from wager (Finished race
      // click) or replay-video ("View Result"). Match on `id` first,
      // then fall back to `TrackId` — the replays hand-off carries
      // the track id in the `eventId` slot. Consumed once; later
      // emissions fall back to the finished[0] default.
      const hint = this.pendingInitialSelection?.eventId;
      const preSelected = hint
        ? _.find(
            finished,
            (event) =>
              event?.id === hint
              || event?.ToteResponse?.EventDetail?.TrackId === hint
          )
        : undefined;

      this.selectedEvent = preSelected || finished[0];

      if (preSelected && this.pendingInitialSelection?.raceId) {
        const preRace = _.find(
          preSelected?.ToteResponse?.EventDetail?.Races,
          (race: IRace) => race?.RaceId === this.pendingInitialSelection?.raceId
        );
        this.selectedRace = preRace || this.pickFirstFinishedRace(this.selectedEvent);
      } else {
        this.selectedRace = this.pickFirstFinishedRace(this.selectedEvent);
      }

      this.pendingInitialSelection = undefined;
      this.pushSelectionToStore();
      // Center the pagination on the auto-selected race after full load
      this.scrollToSelectedRace();
      this.loadResultsForCurrent();
    }

    this.cdRef.markForCheck();
  }

  /**
   * Sync favorite ids from account preferences into local state
   * @returns {void}
   */
  private observeFavoritesPreference(): void {
    this.wagerService.observerPreferenceFavorites()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((listFavorites?: string[]) => {
        this.setListFavorites = new Set(listFavorites || []);
        this.rebuildFavoriteEvents();

        // First emission = favorites loaded (even if empty).
        this.hasEverLoadedFavorites = true;
        this.cdRef.markForCheck();
      });
  }

  /**
   * Rebuild favorites chips from intersection of favorites and allEvents
   * @returns {void}
   */
  private rebuildFavoriteEvents(): void {
    if (!this.setListFavorites || this.setListFavorites.size === 0) {
      this.listOfFavoriteEvents = [];
      return;
    }

    // OLD: intersected favorites with `allEvents` (becomes past-date
    // events while viewing history), so chips didn't match the wager page.
    // this.listOfFavoriteEvents = _.filter(
    //   this.allEvents,
    //   (event: IGetEventsDetailsResponse) => this.setListFavorites.has(event?.id as string)
    // );
    // NEW: intersect with the live store so favorites mirror the wager page.
    this.listOfFavoriteEvents = _.filter(
      this.liveEvents,
      (event: IGetEventsDetailsResponse) => this.setListFavorites.has(event?.id as string)
    );
  }

  /**
   * Hydrate local tables whenever a result lands for the current race
   * @returns {void}
   */
  private observeResultsAndDetail(): void {
    combineLatest([
      this.wagerFacade.getRaceResult$(),
      this.wagerFacade.getSelectedEventTrack$()
    ])
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe(([result, eventTrack]) => {
        if (!result) return;

        const matchesSelection = (
          result?.ToteResponse?.RaceId === this.raceId
          && result?.ToteResponse?.EventId === this.eventId
        );

        if (!matchesSelection) return;

        this.isFetchingData = Boolean(result?.isGettingResult) && !this.raceResults;

        if (result?.isSuccessResult === false) {
          this.eventResultIsFalse = true;
          this.raceResults = null;
          this.isFetchingData = false;
          // API responded (with failure) — release the skeleton.
          this.hasEverLoadedRaceResult = true;
          this.cdRef.markForCheck();
          return;
        }

        if (result?.isSuccessResult === true) {
          this.applyResultPayload(result, eventTrack);
          // API responded (with success) — release the skeleton.
          this.hasEverLoadedRaceResult = true;
        }

        this.cdRef.markForCheck();
      });
  }

  /**
   * Apply the result payload to template-bound fields
   * @param {ISelectedRaceRaceResult} result API race result payload
   * @param {IGetEventsDetailsResponse} _eventTrack event track (unused)
   * @returns {void}
   */
  private applyResultPayload(
    result: ISelectedRaceRaceResult,
    _eventTrack?: IGetEventsDetailsResponse
  ): void {
    const toteResponse = result?.ToteResponse;
    if (!toteResponse) return;

    this.raceResults = toteResponse;
    this.poolsTotal = (result as any)?.PoolsTotal || [];
    this.personalStatistic = (result as any)?.personalStatistic;
    this.totalHandle = (result as any)?.totalHandle || 0;

    this.poolPrices = this.resultsService.buildDisplayPoolPrices(
      toteResponse?.PoolPrices || []
    );

    // OLD: only this race was rolled up, so a multi-race pool opened by an
    // earlier race had nothing parked to recover its handle from and its
    // Total Pool cell stayed blank.
    // const { fromPoolsTotal, fromTotalHandle } = this.resultsService.buildPoolTotals(
    //   this.storagePoolTotal,
    //   (result as any)?.PoolsTotal || [],
    //   this.totalHandle || 0,
    //   this.poolPrices || [],
    //   this.poolsTotal || [],
    //   toteResponse?.RaceId || 0
    // );
    const {
      fromPoolsTotal,
      fromTotalHandle
    } = this.buildPoolTotalsAcrossRaces(result, toteResponse);

    this.poolsTotal = fromPoolsTotal;
    this.totalHandle = fromTotalHandle;

    this.isFetchingData = false;
  }

  /**
   * Roll every race in the payload through `buildPoolTotals`, then settle on
   * the displayed one. Multi-race pools (DBL, PK3, PK4, ...) report their
   * handle on the race that opens them, so the race that closes one can only
   * recover its Total Pool — matched by pool name — after the earlier races
   * have been parked in `storagePoolTotal`. The Ionic results screens walk
   * `previousEventsResult` the same way.
   * @param {ISelectedRaceRaceResult} result API race result payload
   * @param {IRaceResult} toteResponse Tote response for the displayed race
   * @returns {IBuildPoolTotalsResult} pool totals and handle for this race
   */
  private buildPoolTotalsAcrossRaces(
    result: ISelectedRaceRaceResult,
    toteResponse: IRaceResult
  ): IBuildPoolTotalsResult {
    const forRace = (
      eventPoolTotal: any,
      raceId?: number
    ): IBuildPoolTotalsResult => this.resultsService.buildPoolTotals(
      this.storagePoolTotal,
      eventPoolTotal || [],
      this.totalHandle || 0,
      this.poolPrices || [],
      this.poolsTotal || [],
      raceId || 0
    );

    // Park the sibling races first. Each call is keyed by its own race id, so
    // replaying a race refreshes its entry rather than duplicating it.
    const siblingRaces = (result as any)?.previousEventsResult;
    if (_.isArray(siblingRaces)) {
      _.forEach(siblingRaces, (event: any) => {
        forRace(event?.PoolsTotal, event?.ToteResponse?.RaceId);
      });
    }

    // The displayed race is what the table reads, so it settles the result.
    return forRace((result as any)?.PoolsTotal, toteResponse?.RaceId);
  }

  /**
   * Pick the first finished race; fallback to first available race
   * @param {IGetEventsDetailsResponse} event Selected event
   * @returns {IRace | undefined} Race to display, or undefined
   */
  private pickFirstFinishedRace(
    event?: IGetEventsDetailsResponse
  ): IRace | undefined {
    if (!event) return undefined;
    const races = event?.ToteResponse?.EventDetail?.Races || [];
    return (
      _.find(races, (race: IRace) => race?.RaceStatus === 3)
      || races[0]
      || event?.ToteResponse?.EventDetail?.currentRace
    );
  }

  /**
   * Push current event/race selection into the wager store
   * @returns {void}
   */
  private pushSelectionToStore(): void {
    if (!this.selectedEvent || !this.selectedRace) return;

    this.eventId = this.selectedEvent?.ToteResponse?.EventDetail?.EventId || '';
    this.runId = this.selectedEvent?.ToteResponse?.RunId || '';
    this.raceId = Number(this.selectedRace?.RaceId) || 0;

    this.wagerFacade.selectedEventTrack('ResultsComponent', this.selectedEvent);
    this.wagerFacade.selectedRace('ResultsComponent', {
      ...(this.selectedRace as IRace),
      runId: this.runId,
      eventId: this.eventId
    });
  }

  /**
   * Fetch result payload, event detail, matrix data for the current race
   * @returns {void}
   */
  private loadResultsForCurrent(): void {
    if (!this.eventId || !this.runId || !this.raceId) return;

    this.isFetchingData = true;
    this.raceResults = null;
    this.eventResultIsFalse = false;

    this.wagerFacade.getRaceResultAPI(
      {
        raceId: this.raceId,
        eventId: this.eventId,
        runId: Number(this.runId),
      },
      'ResultsComponent:loadResultsForCurrent'
    );

    this.wagerFacade.getEventDetail(
      'ResultsComponent:loadResultsForCurrent',
      this.eventId,
      this.runId
    );

    this.rebuildMatrixTables();

    this.runners = {
      Runners: _.map(this.selectedRace?.Runners || [], (runner: any) => ({
        Name: _.get(runner, 'Entries[0].Name', ''),
        Id: runner.RunnerId,
        ML: _.get(runner, 'Entries[0].Position', ''),
        Odds: runner.Odds
      }))
    };
  }

  /**
   * Dispatch pool matrix, totals, will pays and odds matrixes for the current race.
   *
   * The replay flags do two things: they stop the reducer from dropping
   * payloads that don't match the store's live `selectedRace` (which the
   * event-detail refresh and pusher keep moving to the current race), and
   * they mirror the payloads into the replay-only slices this page reads.
   * @returns {void}
   */
  private rebuildMatrixTables(): void {
    if (!this.raceId || !this.eventId || !this.runId) return;

    // Wipe every matrix slot so the panel can't briefly render the
    // previous selection's data while the refetch is in flight.
    this.clearMatrixStoreSlots('ResultsComponent:rebuildMatrixTables');

    const poolList = [
      'TRU',
      ...(this.selectedRace?.PoolList?.split(',') || []),
    ];

    _.forEach(poolList, (poolId: string) => {
      if (!poolId) return;

      // OLD: no `replayMatrix`, so the reducer dropped these pools
      // whenever the store had already moved on to the live race.
      // this.wagerFacade.getPoolMatrix(
      //   'ResultsComponent:rebuildMatrixTables',
      //   { raceId: this.raceId, eventId: this.eventId, runId: this.runId, poolId }
      // );
      this.wagerFacade.getPoolMatrix(
        'ResultsComponent:rebuildMatrixTables',
        {
          raceId: this.raceId,
          eventId: this.eventId,
          runId: this.runId,
          poolId,
          replayMatrix: true
        }
      );
    });

    this.wagerFacade.getPoolsTotals(
      'ResultsComponent:rebuildMatrixTables',
      {
        raceId: this.raceId,
        eventId: this.eventId,
        runId: this.runId,
        replay: true
      }
    );

    this.wagerFacade.getWillPays(
      'ResultsComponent:rebuildMatrixTables',
      {
        raceId: this.raceId,
        eventId: this.eventId,
        runId: this.runId,
        replay: true
      }
    );

    _.forEach(['WIN', 'TRU'] as const, (pool) => {
      this.wagerFacade.getRaceAllOddsMatrixes(
        'ResultsComponent:rebuildMatrixTables',
        pool,
        {
          raceId: this.raceId,
          eventId: this.eventId,
          runId: this.runId,
          poolId: pool,
          // OLD: no `replay`, so the slice was indistinguishable from a live
          // pusher write and the Win Chart repainted with the current race.
          replay: true
        }
      );
    });
  }

  /**
   * Track change — apply new event, snap to first finished race, refresh tables
   * @param {IGetEventsDetailsResponse} event Selected event
   * @returns {void}
   */
  selectEvent(event: IGetEventsDetailsResponse): void {
    if (!event || this.isFetchingData) return;
    if (this.selectedEvent?.id === event.id) return;

    this.selectedEvent = event;
    this.selectedRace = this.pickFirstFinishedRace(event);

    this.resetForNewSelection();
    this.pushSelectionToStore();

    // Center the pagination on the new track's selected race
    this.scrollToSelectedRace();

    if (this.raceResultTimeout) clearTimeout(this.raceResultTimeout);
    this.raceResultTimeout = setTimeout(() => this.loadResultsForCurrent(), 0);

    this.cdRef.markForCheck();
  }

  /**
   * Favorites chip — redirect to the wager page with this event track
   * (and its current race) pre-selected.
   * @param {IGetEventsDetailsResponse} favorite selected favorite event
   * @returns {void}
   */
  selectFavorite(favorite: IGetEventsDetailsResponse): void {
    if (!favorite) return;

    this.router.navigate(['/wager'], {
      state: {
        eventId: favorite?.id,
        runId: favorite?.ToteResponse?.RunId,
        raceId: favorite?.ToteResponse?.EventDetail?.currentRace?.RaceId,
      }
    });
  }

  /**
   * Auto-scroll the race pagination so the selected race is centered.
   * Deferred so the new event's race chips render before we measure.
   * @returns {void}
   */
  private scrollToSelectedRace(): void {
    setTimeout(() => {
      this.raceService.scrollToPage(
        this.selectedRace?.RaceId || 0,
        this.swipeRaceSectionTemplateRef
      );
    }, 100);
  }

  /**
   * Race pagination — click a race chip to reload results and matrix.
   * Non-Finished race → hand off to wager page with this event/race
   * pre-selected (mirrors wager's Finished-race → results redirect).
   * @param {IRace} race Selected race
   * @returns {void}
   */
  selectRace(race: IRace): void {
    if (!race || this.isFetchingData) return;
    if (this.selectedRace?.RaceId === race.RaceId) return;

    // Open (not Finished) race → jump to wager with pre-selection.
    if (race?.RaceStatus !== 3) {
      this.router.navigate(['/wager'], {
        state: {
          eventId: this.selectedEvent?.id,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          raceId: race?.RaceId,
        }
      });
      return;
    }

    this.selectedRace = race;
    this.resetForNewSelection();
    this.pushSelectionToStore();

    // Center the pagination on the clicked race
    this.scrollToSelectedRace();

    if (this.raceResultTimeout) clearTimeout(this.raceResultTimeout);
    this.raceResultTimeout = setTimeout(() => this.loadResultsForCurrent(), 0);

    this.cdRef.markForCheck();
  }

  /**
   * Date picker change — moves the results view to a different
   * date and re-requests the events list. Guards against invalid
   * or future dates, and against duplicate emits for the same day
   * (initial paint + `[value]` echo both fire `dateChange`).
   */
  setDate(event: any): void {
    const rawValue = event?.value ?? event?.target?.value;
    if (!rawValue) return;

    const parsed = moment(rawValue);
    if (!parsed.isValid() || parsed.isAfter(moment(), 'day')) {
      this.invalidDate = true;
      this.cdRef.markForCheck();
      return;
    }

    // Skip when the picked day matches what's already displayed —
    // avoids a redundant refetch on the initial `dateChange` echo.
    const newDateStr = parsed.format('MM-DD-YYYY');
    if (newDateStr === this.selectedDate && !this.invalidDate) return;

    this.invalidDate = false;
    this.selectedDate = newDateStr;
    // Push a real `Date` so the datepicker keeps the value on screen.
    this.formVal['dateBy']?.setValue(parsed.toDate());

    this.isFetchingEventsData = true;
    this.selectedEvent = undefined;
    this.selectedRace = undefined;
    this.resetForNewSelection();

    if (parsed.isSame(moment(), 'day')) {
      // Today → rejoin the shared store snapshot.
      this.isViewingPastDate = false;
      this.reloadFromStore();
    } else {
      // Historical date → fetch by date without touching the store.
      this.isViewingPastDate = true;
      this.fetchEventsByDate(newDateStr);
    }

    this.cdRef.markForCheck();
  }

  /**
   * Reset date filter to today
   * @returns {void}
   */
  todayDate(): void {
    if (this.isFetchingData) return;

    this.selectedDate = moment().format('MM-DD-YYYY');
    this.formVal['dateBy']?.setValue(new Date());

    this.isFetchingEventsData = true;
    this.selectedEvent = undefined;
    this.selectedRace = undefined;
    this.resetForNewSelection();

    // Rejoin the shared store; today's events live there already.
    this.isViewingPastDate = false;
    this.reloadFromStore();
    this.cdRef.markForCheck();
  }

  /**
   * Pull the current store events snapshot and feed it through
   * {@link processEvents} — used when leaving a historical date,
   * where the store selector won't re-emit on its own because the
   * underlying entities haven't changed.
   * @returns {void}
   */
  private reloadFromStore(): void {
    this.wagerFacade.getSelectAllEventTrackRace$()
      .pipe(take(1), takeUntil(this.toUnsubscribe$))
      .subscribe((allEvents?: IGetEventsDetailsResponse[]) => {
        // Guard against a race: user picked another past date before
        // this snapshot landed.
        if (this.isViewingPastDate) return;
        this.processEvents(allEvents);
      });
  }

  /**
   * Fetch events + full details for a historical date and feed them
   * through {@link processEvents} — mirrors the mobile results flow.
   * State is kept local to the component so today's shared events
   * aren't clobbered.
   * @param {string} dateStr Date string in `MM-DD-YYYY` format
   * @returns {void}
   */
  private fetchEventsByDate(dateStr: string): void {
    this.toteProgramAPIService.getAllEventsByDate$(dateStr)
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe({
        next: (response) => {
          // API wraps the payload in { Model: ... } — unwrap defensively.
          const model = (response as any)?.Model ?? response;
          const runId = model?.ToteResponse?.RunId;

          // Bail out on empty/invalid dates — API still hits 200 with no run.
          if (this.selectedDate !== dateStr) return;
          if (!runId) {
            this.processEvents([]);
            return;
          }

          this.toteProgramAPIService.getEventsDetails$(runId)
            .pipe(takeUntil(this.toUnsubscribe$))
            .subscribe({
              next: (detailsResponse) => {
                if (this.selectedDate !== dateStr) return;

                const raw = ((detailsResponse as any)?.Model ?? detailsResponse) as
                  IGetEventsDetailsResponse[] | undefined;
                this.processEvents(this.resultsService.normalizeEvents(raw));
              },
              error: () => {
                if (this.selectedDate !== dateStr) return;
                this.processEvents([]);
              }
            });
        },
        error: () => {
          if (this.selectedDate !== dateStr) return;
          this.processEvents([]);
        }
      });
  }

  /**
   * Whether the Replay launcher should be clickable
   * @returns {boolean} true when replay is allowed
   */
  isReplayAllowed(): boolean {
    return Boolean(
      this.selectedEvent
      && this.selectedRace
      && !this.isFetchingData
      && !this.isFetchingEventsData
    );
  }

  /**
   * Fire the replay-video request and stash metadata for the dialog
   * @returns {void}
   */
  openReplayVideo(): void {
    if (!this.isReplayAllowed()) return;
    // Bail out if a replay is already up or an open is in flight —
    // otherwise repeated clicks would stack duplicate dialogs
    if (this.replayDialogRef || this.replayPending) return;

    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('ResultsComponent:openReplayVideo', false);
      return;
    }

    this.replayPending = true;

    this.wagerFacade.getEventRaceVideoReplay(
      'ResultsComponent:openReplayVideo',
      {
        toteEventId: this.selectedEvent?.id,
        toteRaceId: this.selectedRace?.RaceId,
        raceDay: this.resultsService.toDateUTC(this.selectedDate),
        width: 640,
        height: 500
      }
    );

    this.wagerFacade.setSelectedReplayInfo(
      this.selectedEvent?.ToteResponse?.EventDetail?.TrackName as any,
      this.selectedEvent?.ToteResponse?.EventDetail?.EventTime as any,
      TRACK_TYPE_lIST[this.selectedRace?.TrackType as number],
      this.selectedRace?.RaceId as any,
      this.selectedEvent?.ToteResponse?.RunId,
      this.selectedEvent?.id,
      'WIN',
      true
    );
  }

  /**
   * Open the replay dialog once the replay video URL arrives
   * @returns {void}
   */
  private observeReplayVideo(): void {
    this.wagerFacade.getRaceResult$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((result?: any) => {
        const replayVideo: string | undefined = result?.replayVideo;

        if (!replayVideo) {
          // The replay API returns HTTP 200 with an `Error` body (no failed
          // action fires), so an empty/errored replay would otherwise leave
          // the guard stuck — release it here so the icon/chip work again.
          if (this.replayPending && result?.errorMsg) this.replayPending = false;
          return;
        }
        if (!this.replayPending) return;

        // Belt-and-braces: never stack a second replay dialog on
        // top of one that's still open (edge race with rapid clicks)
        if (this.replayDialogRef) {
          this.replayPending = false;
          return;
        }

        this.replayPending = false;

        // min-height keeps the title, video and runners table
        // visible; it still grows if the content is taller.
        this.replayDialogRef = this.dialog.open(ReplayVideoComponent, {
          data: replayVideo,
          width: '500px',
          minHeight: '600px',
          hasBackdrop: false,
          disableClose: false,
          panelClass: 'replay-video-dialog'
        });

        this.replayDialogRef.afterClosed().subscribe(() => {
          this.replayDialogRef = undefined;
        });
      });

    // A video/replay API error (`from: true`) doesn't resolve the result
    // stream, so release the guard here — otherwise the replay icon /
    // matrix Video chip stay stuck after the error message is dismissed.
    this.commonFacade.isErrorAPI$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((error: any) => {
        if (error?.from === true) this.replayPending = false;
      });
  }

  /**
   * Clear transient result tables so loading state paints on the next selection
   * @returns {void}
   */
  private resetForNewSelection(): void {
    this.raceResults = null;
    this.poolsTotal = [];
    this.poolPrices = [];
    this.totalHandle = 0;
    this.personalStatistic = undefined;
    this.eventResultIsFalse = false;
    this.errMsg = '';

    this.clearMatrixStoreSlots('ResultsComponent:resetForNewSelection');
  }

  /**
   * Drop the replay matrix slots so the Pool Data panel can't paint the
   * previous race's odds / totals / will-pays while a new fetch is in
   * flight.
   *
   * Only the replay mirrors are cleared: the live slices belong to the
   * wager page and pusher keeps writing to them, so wiping them from here
   * would both stall live data and hand our panel a slot that the next
   * pusher update refills with the current race.
   * @param {string} sourceInitiator Trace label for the dispatch
   * @returns {void}
   */
  private clearMatrixStoreSlots(sourceInitiator: string): void {
    // OLD: cleared the slices shared with the live wager page, so results
    // and pusher fought over the same slots.
    // this.wagerFacade.clearSelectedRaceMatrix(sourceInitiator);
    // this.wagerFacade.clearSelectedRacePoolsTotals(sourceInitiator);
    // this.wagerFacade.clearSelectedRaceWillPays(sourceInitiator);
    // this.wagerFacade.clearRaceAllOddsMatrixes(sourceInitiator);
    this.wagerFacade.clearReplayMatrix(sourceInitiator);
  }

  /**
   * `mat-select` comparator for object option values
   * @param {any} o1 first option
   * @param {any} o2 second option
   * @returns {boolean} true if the two options match
   */
  compareWith(o1: any, o2: any): boolean {
    return o1 && o2 ? o1.id === o2.id : o1 === o2;
  }

  /**
   * `*ngFor` trackBy using the stable event id
   * @param {number} _index list index (unused)
   * @param {any} item Event item
   * @returns {number} stable event id
   */
  trackByFn(_index: number, item: any): number {
    return item?.id;
  }

  /**
   * Scroll the favorites row by 200px in the given direction
   * @param {'prev' | 'next'} direction which side to scroll
   * @returns {void}
   */
  scrollTo(direction: 'prev' | 'next'): void {
    const container = this.favorites?.nativeElement;
    if (!container) return;

    const scrollAmount = 200;
    if (direction === 'prev') {
      container.scrollLeft -= scrollAmount;
    } else {
      container.scrollLeft += scrollAmount;
    }
  }
}
